import PropTypes from "prop-types";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import * as dayjs from "dayjs";

import {
    ACTION_CREATE,
    ACTION_DELETE,
    ACTION_UPDATE,
    DATA_EMPTY,
    DATA_FAILED
} from "../../../component/Constants";
import ConditionalRender from "../../../helpers/ConditionalRender";
import IntegrationType from "../../../types/integration/IntegrationType";
import {
    buildMakeDefaultPayloads,
    getNextProfilePrefix,
    NotificationIntegrationConfig,
    NotificationIntegrationProfile,
    parseNotificationProfiles,
    remapProfileParameterName
} from "../../../helpers/notificationIntegrations";
import api from "../../../api/Base";
import { showError } from "../../../utils/showAlert";

function ProfileActionsDropdown(props: {
    basePath: string,
    profile: NotificationIntegrationProfile,
    onDuplicate: () => void
}) {
    const [show, setShow] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number, right: number }>({ top: 0, right: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    useLayoutEffect(() => {
        if (!show || !buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom,
            right: window.innerWidth - rect.right
        });
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                buttonRef.current && !buttonRef.current.contains(target) &&
                menuRef.current && !menuRef.current.contains(target)
            ) {
                setShow(false);
            }
        };
        const handleScrollOrResize = () => setShow(false);
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [show]);

    return (
        <div className="dropdown" style={{ display: 'inline-block' }}>
            <button
                ref={buttonRef}
                type="button"
                className="btn btn-sm btn-secondary dropdown-toggle"
                onClick={() => setShow(!show)}
                aria-expanded={show}
            >
                <i className="fal fa-ellipsis-h fa-fw"/><span>Actions</span>
            </button>
            {show && createPortal(
                <ul ref={menuRef}
                    className="dropdown-menu show"
                    style={{
                        display: 'block',
                        position: 'fixed',
                        top: menuPosition.top,
                        right: menuPosition.right,
                        left: 'auto',
                        zIndex: 1050
                    }}>
                    <li>
                        <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                                props.onDuplicate();
                                setShow(false);
                            }}
                        >
                            <i className="fal fa-copy fa-fw me-2"/>Duplicate
                        </button>
                    </li>
                    <li>
                        <Link
                            className="dropdown-item text-danger"
                            to={`${props.basePath}/delete/${props.profile.prefix}`}
                            state={{ action: ACTION_DELETE }}
                            onClick={() => setShow(false)}
                        >
                            <i className="fal fa-trash-alt fa-fw me-2"/>Delete
                        </Link>
                    </li>
                </ul>,
                document.body
            )}
        </div>
    );
}

function NotificationIntegrationList(props: {
    loading: number,
    parameters: IntegrationType[],
    refresh: Function,
    isAdmin: boolean,
    config: NotificationIntegrationConfig,
    basePath: string,
    iconClass: string,
    endpointLabel: string,
    endpointKey: string
}) {
    const profiles = parseNotificationProfiles(props.parameters, props.config);

    function refresh() {
        props.refresh();
    }

    const makeDefault = async(prefix: string) => {
        const { deleteValues, postValues } = buildMakeDefaultPayloads(props.parameters, props.config, prefix);

        if (deleteValues.length > 0) {
            await api.delete('/parameter', { data: deleteValues });
        }

        if (postValues.length > 0) {
            await api.post('/parameter', postValues);
        }

        props.refresh();
    };

    const duplicateProfile = async (sourceProfile: NotificationIntegrationProfile) => {
        const existingNames = profiles.map(profile => profile.label);
        const baseName = sourceProfile.label;
        let newName = `${baseName} copy`;
        let counter = 2;
        while (existingNames.includes(newName)) {
            newName = `${baseName} copy ${counter}`;
            counter++;
        }

        const newPrefix = getNextProfilePrefix(props.parameters, props.config);
        const dbmarlinNameKey = `${newPrefix}.dbmarlin.name`;
        const enableKey = `${newPrefix}.enable`;

        let hasName = false;
        const postValues: { name: string; value: any; encrypted: boolean }[] = sourceProfile.parameters.map(parameter => {
            const name = remapProfileParameterName(parameter.name, sourceProfile.prefix, newPrefix);
            let value: any = parameter.value;
            if (name === dbmarlinNameKey) {
                hasName = true;
                value = newName;
            } else if (name === enableKey) {
                value = 'false';
            }
            return { name, value, encrypted: true };
        });

        if (!hasName) {
            postValues.push({ name: dbmarlinNameKey, value: newName, encrypted: true });
        }

        try {
            const result = await api.post('/parameter', postValues);
            if (result.status === 200) {
                props.refresh();
            }
        } catch (error: any) {
            console.error(`Failed to duplicate ${props.config.label} integration`, error);
            showError(`Failed to duplicate ${props.config.label} integration`, error.message || error.toString());
        }
    };

    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const sortedProfiles = useMemo(() => {
        return [...profiles].sort((a, b) => {
            const nameA = (a.label || '').toLocaleLowerCase();
            const nameB = (b.label || '').toLocaleLowerCase();

            if (nameA < nameB) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (nameA > nameB) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [profiles, sortDirection]);

    const data = sortedProfiles.map(profile => ({
        label: profile.label,
        prefix: profile.prefix,
        default: profile.isDefault ? 'Yes' : 'No',
        endpoint: profile.values[props.endpointKey] || '',
        dbmarurl: profile.values['dbmarlin.url'] || '',
        enabled: profile.enabled
    }));

    const toggleSortDirection = () => {
        setSortDirection(direction => direction === 'asc' ? 'desc' : 'asc');
    };

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className={`${props.iconClass} fa-fw`} aria-hidden="true"/>
                    {props.config.label}
                    <span className="badge bg-info"
                          data-tooltip-id="global-tooltip"
                          data-tooltip-content={`Total ${props.config.label} integration count`}>{profiles.length}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target={`#collapse${props.config.kind}`} aria-expanded="false" aria-controls={`collapse${props.config.kind}`}/>
                </div>
                <div id={`collapse${props.config.kind}`} className="card-body collapse show">
                    {props.loading === DATA_EMPTY ? (
                        <div className="w-100 text-center text-muted my-3">
                            <i className="fal fa-ban fa-fw fa-2x"/>
                            <p>
                                <button type="button" className="btn btn-link" onClick={refresh}>
                                    {props.config.label} integration not found - Click to Refresh
                                </button>
                            </p>
                        </div>
                    ) : (
                        props.loading === DATA_FAILED ? (
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x text-danger"/>
                                <p>
                                    <button type="button" className="btn btn-link" onClick={refresh}>
                                        Failed to Retrieve {props.config.label} - Click to Refresh
                                    </button>
                                </p>
                            </div>
                        ) : (
                            <React.Fragment>
                                <div className="row row-cols-1 row-cols-md-2 table-search">
                                    <div className="col col-md-12">
                                        <ConditionalRender if={props.isAdmin}>
                                            <Link to={`${props.basePath}/create`}
                                                  state={{ action: ACTION_CREATE }}
                                                  role="button" className="btn btn-sm btn-success">
                                                <i className="fal fa-plus-square fa-fw"/><span>Create</span>
                                            </Link>
                                        </ConditionalRender>

                                        <CSVLink role="button"
                                                 headers={[
                                                     { label: 'Name', key: 'label' },
                                                     { label: 'Prefix', key: 'prefix' },
                                                     { label: 'Default', key: 'default' },
                                                     { label: props.endpointLabel, key: 'endpoint' },
                                                     { label: 'DBmarlin URL', key: 'dbmarurl' },
                                                     { label: 'Enabled', key: 'enabled' }
                                                 ]}
                                                 data={data}
                                                 download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - ${props.config.label}.csv`}
                                                 className="btn btn-sm btn-primary">
                                            <i className="fal fa-file-export fa-fw"/><span>Export</span>
                                        </CSVLink>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table">
                                        <thead>
                                        <tr>
                                            <th scope="col"
                                                role="button"
                                                style={{ cursor: 'pointer' }}
                                                onClick={toggleSortDirection}
                                                title="Sort by name">
                                                Name {sortDirection === 'desc' ?
                                                    <i className="fal fa-sort-amount-up-alt"/> :
                                                    <i className="fal fa-sort-amount-down-alt"/>}
                                            </th>
                                            <th scope="col">Default</th>
                                            <th scope="col">{props.endpointLabel}</th>
                                            <th scope="col">DBmarlin URL</th>
                                            <th scope="col">Enabled</th>
                                            <th className="actions" scope="col">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {sortedProfiles.map(profile => (
                                            <tr key={profile.prefix}>
                                                <td>{profile.label}</td>
                                                <td>
                                                    <span className={`badge ${profile.isDefault ? 'bg-success text-white' : 'bg-secondary text-white'} py-1 px-2`} style={{ fontSize: '0.87rem' }}>
                                                        {profile.isDefault ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="concatenate">{profile.values[props.endpointKey]}</td>
                                                <td className="concatenate">{profile.values['dbmarlin.url']}</td>
                                                <td className="concatenate">
                                                    <div className='form-check form-switch'>
                                                        <input checked={profile.enabled}
                                                               type="checkbox"
                                                               className="form-check-input"
                                                               disabled={true}
                                                               data-lpignore={true}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="actions">
                                                    {props.isAdmin && (
                                                        <div className="d-flex gap-1 align-items-center justify-content-end">
                                                            {!profile.isDefault && (
                                                                <button type="button"
                                                                        className="btn btn-sm btn-secondary"
                                                                        onClick={() => makeDefault(profile.prefix)}>
                                                                    <i className="fal fa-star fa-fw"/><span>Make default</span>
                                                                </button>
                                                            )}
                                                            <Link to={`${props.basePath}/edit/${profile.prefix}`}
                                                                  state={{ action: ACTION_UPDATE }}
                                                                  role="button" className="btn btn-sm btn-primary">
                                                                <i className="fal fa-edit fa-fw"/><span>Edit</span>
                                                            </Link>
                                                            <ProfileActionsDropdown
                                                                basePath={props.basePath}
                                                                profile={profile}
                                                                onDuplicate={() => duplicateProfile(profile)}
                                                            />
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </React.Fragment>
                        )
                    )}
                </div>
            </div>
        </div>
    )
}

NotificationIntegrationList.propTypes = {
    refresh: PropTypes.func.isRequired,
};

export default NotificationIntegrationList;
